import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Car } from 'lucide-react';
import { CarpoolVehicle } from '../types';
import baserowApi from '../services/baserowApi';

interface DamageReport {
  id: number;
  title: string;
  description?: string;
  status: { id: number; value: string; color: string };
  carpool: Array<{ id: number; value: string }>;
  estimated_costs: string;
  real_costs: string;
  created_at?: string; // Optional gemacht, da es undefined sein kann
}

// Kategorien für Projekte
const CATEGORY_OPTIONS = [
  'Schaden',
  'Termin', 
  'Service & Inspektion',
] as const;
type ProjectCategory = typeof CATEGORY_OPTIONS[number];

function extractCategoryFromDescription(description: string): ProjectCategory | null {
  if (!description) return null;
  const match = description.match(/^Kategorie:\s*(.+)$/m);
  if (match) {
    const value = match[1].trim();
    if ((CATEGORY_OPTIONS as readonly string[]).includes(value)) {
      return value as ProjectCategory;
    }
  }
  return null;
}

interface DamageCostsOverviewProps {
  vehicles: CarpoolVehicle[];
}

interface VehicleProjectCosts {
  vehicleId: number;
  vehicleName: string;
  licensePlate: string;
  totalEstimatedCosts: number;
  totalRealCosts: number;
  damageCount: number;
  terminCount: number;
  serviceCount: number;
  totalProjectCount: number;
}

const DamageCostsOverview: React.FC<DamageCostsOverviewProps> = ({ vehicles }) => {
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [vehicleCosts, setVehicleCosts] = useState<VehicleProjectCosts[]>([]);
  const [totalYearlyCosts, setTotalYearlyCosts] = useState({
    estimated: 0,
    real: 0
  });

  // Lade Schadensmeldungen beim Mount
  useEffect(() => {
    const loadDamageReports = async () => {
      try {
        setLoading(true);
        const response = await baserowApi.getDamageReports();
        console.log('API Response für Schadensmeldungen:', response);
        setDamageReports(response.results);
      } catch (error) {
        console.error('Fehler beim Laden der Schadensmeldungen:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDamageReports();
  }, []);

  // Berechne Kosten pro Fahrzeug und Gesamtkosten
  useEffect(() => {
    if (damageReports.length === 0) return;

    const currentYear = new Date().getFullYear();
    console.log('Aktuelles Jahr:', currentYear);
    console.log('Geladene Schadensmeldungen:', damageReports);
    
    const costsByVehicle = new Map<number, VehicleProjectCosts>();

    // Initialisiere alle Fahrzeuge mit 0 Kosten
    vehicles.forEach(vehicle => {
      costsByVehicle.set(vehicle.id, {
        vehicleId: vehicle.id,
        vehicleName: vehicle.model,
        licensePlate: vehicle.license_plate,
        totalEstimatedCosts: 0,
        totalRealCosts: 0,
        damageCount: 0,
        terminCount: 0,
        serviceCount: 0,
        totalProjectCount: 0
      });
    });

    let totalEstimated = 0;
    let totalReal = 0;

    // Berechne Kosten für jedes Projekt
    damageReports.forEach(project => {
      console.log('Verarbeite Projekt:', project);
      console.log('created_at:', project.created_at);
      
      // Extrahiere Kategorie aus Beschreibung
      const category = extractCategoryFromDescription(project.description || '');
      console.log('Projekt Kategorie:', category);
      
      // Nur Projekte mit gültiger Kategorie berücksichtigen
      if (!category) {
        console.log('Projekt hat keine gültige Kategorie, wird ignoriert');
        return;
      }
      
      // Fallback-Logik für fehlende created_at Daten
      let projectYear = currentYear; // Standard: aktuelles Jahr
      
      if (project.created_at) {
        const projectDate = new Date(project.created_at);
        const calculatedYear = projectDate.getFullYear();
        
        // Prüfe ob das Jahr gültig ist (nicht NaN)
        if (!isNaN(calculatedYear)) {
          projectYear = calculatedYear;
        }
      }
      
      console.log('Projekt Jahr:', projectYear, 'Aktuelles Jahr:', currentYear);
      
      // Nur Projekte aus dem aktuellen Jahr berücksichtigen
      if (projectYear === currentYear) {
        console.log('Projekt ist aus aktuellem Jahr, wird verarbeitet');
        const estimatedCost = parseFloat(project.estimated_costs) || 0;
        const realCost = parseFloat(project.real_costs) || 0;
        console.log('Geschätzte Kosten:', estimatedCost, 'Tatsächliche Kosten:', realCost);

        // Alle Projekte für Kostenberechnung verwenden
        totalEstimated += estimatedCost;
        totalReal += realCost;

        // Füge Projekt-Zähler zum entsprechenden Fahrzeug hinzu
        if (project.carpool && project.carpool.length > 0) {
          const vehicleId = project.carpool[0].id;
          console.log('Fahrzeug ID:', vehicleId);
          const vehicleCosts = costsByVehicle.get(vehicleId);
          
          if (vehicleCosts) {
            // Alle Projekte für Kosten zählen
            vehicleCosts.totalEstimatedCosts += estimatedCost;
            vehicleCosts.totalRealCosts += realCost;
            
            // Kategorien-spezifische Zähler
            switch (category) {
              case 'Schaden':
                vehicleCosts.damageCount += 1;
                break;
              case 'Termin':
                vehicleCosts.terminCount += 1;
                break;
              case 'Service & Inspektion':
                vehicleCosts.serviceCount += 1;
                break;
            }
            vehicleCosts.totalProjectCount += 1;
            
            console.log('Projekt zu Fahrzeug hinzugefügt:', vehicleCosts);
          } else {
            console.log('Fahrzeug nicht gefunden für ID:', vehicleId);
          }
        } else {
          console.log('Kein Fahrzeug zugeordnet für Projekt:', project.id);
        }
      } else {
        console.log('Projekt ist nicht aus aktuellem Jahr, wird ignoriert');
      }
    });

    console.log('Finale Fahrzeugkosten:', Array.from(costsByVehicle.values()));
    console.log('Gesamtkosten - Geschätzt:', totalEstimated, 'Tatsächlich:', totalReal);

    setVehicleCosts(Array.from(costsByVehicle.values()));
    setTotalYearlyCosts({
      estimated: totalEstimated,
      real: totalReal
    });
  }, [damageReports, vehicles]);

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Filtere nur Fahrzeuge mit Projekten (Schäden, Termine, Service)
  const vehiclesWithProjects = vehicleCosts.filter(vehicle => vehicle.totalProjectCount > 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-[#add015]" />
          <h2 className="text-lg font-semibold text-gray-900">
            Kostenübersicht Fahrzeugmanagement {new Date().getFullYear()}
          </h2>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Gesamtkosten geschätzt:</span>
            <span className="font-medium text-blue-600">
              {totalYearlyCosts.estimated.toFixed(2)} €
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">Gesamtkosten tatsächlich:</span>
            <span className="font-medium text-[#add015]">
              {totalYearlyCosts.real.toFixed(2)} €
            </span>
          </div>
        </div>
      </div>

      {vehiclesWithProjects.length === 0 ? (
        <div className="text-center py-8">
          <Car className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            Keine Projekte für {new Date().getFullYear()} vorhanden.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 font-medium text-gray-700">Fahrzeug</th>
                <th className="text-right py-2 font-medium text-gray-700">Schäden</th>
                <th className="text-right py-2 font-medium text-gray-700">Termine</th>
                <th className="text-right py-2 font-medium text-gray-700">Service</th>
                <th className="text-right py-2 font-medium text-gray-700">Geschätzt</th>
                <th className="text-right py-2 font-medium text-gray-700">Tatsächlich</th>
                <th className="text-right py-2 font-medium text-gray-700">Differenz</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vehiclesWithProjects.map((vehicle) => {
                const difference = vehicle.totalRealCosts - vehicle.totalEstimatedCosts;
                const differenceColor = difference > 0 ? 'text-red-600' : 'text-[#add015]';
                const differenceSign = difference > 0 ? '+' : '';
                
                return (
                  <tr key={vehicle.vehicleId} className="hover:bg-gray-50">
                    <td className="py-2">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-gray-400" />
                        <div>
                          <div className="font-medium text-gray-900">
                            {vehicle.vehicleName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {vehicle.licensePlate}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="text-right py-2 text-red-600 font-medium">
                      {vehicle.damageCount}
                    </td>
                    <td className="text-right py-2 text-blue-600 font-medium">
                      {vehicle.terminCount}
                    </td>
                    <td className="text-right py-2 text-emerald-600 font-medium">
                      {vehicle.serviceCount}
                    </td>
                    <td className="text-right py-2 text-blue-600 font-medium">
                      {vehicle.totalEstimatedCosts.toFixed(2)} €
                    </td>
                    <td className="text-right py-2 text-[#add015] font-medium">
                      {vehicle.totalRealCosts.toFixed(2)} €
                    </td>
                    <td className={`text-right py-2 font-medium ${differenceColor}`}>
                      {differenceSign}{difference.toFixed(2)} €
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Zusammenfassung - nur anzeigen wenn Schäden vorhanden */}
      {vehiclesWithProjects.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                <span className="text-gray-500">Gesamtkosten geschätzt:</span>
                <span className="font-medium text-blue-600">
                  {totalYearlyCosts.estimated.toFixed(2)} €
                </span>
              </div>
              <div className="flex items-center gap-1">
                <DollarSign className="w-4 h-4 text-[#add015]" />
                <span className="text-gray-500">Gesamtkosten tatsächlich:</span>
                <span className="font-medium text-[#add015]">
                  {totalYearlyCosts.real.toFixed(2)} €
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-500">Differenz:</span>
              <span className={`font-medium ${totalYearlyCosts.real > totalYearlyCosts.estimated ? 'text-red-600' : 'text-[#add015]'}`}>
                {(totalYearlyCosts.real - totalYearlyCosts.estimated).toFixed(2)} €
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DamageCostsOverview; 